import { ChapterPageManager } from "~/src/reader/managers/chapterManager";
import {
  type AvailableLanguages,
  type ChapterImageQualities,
  type TitleChapters,
} from "~/src/api";
import { isContentPageGroup, isImagePageGroup } from "~/src/reader/utils";
import {
  type ImagePageGroup,
  InterludePageGroup,
  type PageGroupTypes,
  PlaceholderPageGroup,
  SubscribeLockedPageGroup,
} from "~/src/reader/managers/pageGroup";
import { QueryClient } from "@tanstack/vue-query";
import { getChapterFromCache } from "~/utils/tanstack/chapter";
import type { UnwrapNestedRefs } from "vue";

const debugStyle =
  "color:black;background-color:#ff99ff;padding:2px 4px;border-radius:4px;";
const debug = (...args: any[]) =>
  console.debug("%c%s", debugStyle, "Reader", ...args);
const debugChapterId = (chapterId: string, ...args: any[]) =>
  console.debug(
    "%cReader%c Chapter with id %c%s%c",
    debugStyle,
    "",
    "font-weight:bold",
    chapterId,
    "",
    ...args,
  );

export function getChapterSequenceForLang(
  aggregate: TitleChapters,
  lang: AvailableLanguages,
) {
  // get null vol if any ahead of time
  const nullVol = aggregate.attributes.list.find((vol) => vol.volume === null);

  const langChapters = aggregate.attributes.list
    // remove null vol
    .filter((vol) => vol.volume !== null)
    .flatMap((vol) => vol.chapters);
  const chapters = langChapters.filter((ch) => ch.translatedLanguage === lang);

  // add all null vol chapters to the end except oneshots
  const nullVolChapters =
    nullVol?.chapters
      .filter((ch) => ch.translatedLanguage === lang)
      // remove oneshots
      .filter((ch) => ch.chapter !== null) ?? [];
  chapters.push(...nullVolChapters);

  // add the oneshot(s) in at the beginning if any exist
  const oneshotsInLang =
    nullVol?.chapters.filter(
      (ch) => ch.translatedLanguage === lang && !ch.chapter,
    ) ?? [];
  if (oneshotsInLang.length) chapters.unshift(...oneshotsInLang);

  return chapters;
}

export function getAvailableLanguageVariants(
  aggregate: TitleChapters,
  chapterId: string,
) {
  const currentVol = aggregate.attributes.list.find((vol) =>
    vol.chapters.some((ch) => ch.id === chapterId),
  );

  if (!currentVol) return [];
  const currChapter = currentVol.chapters.find((ch) => ch.id === chapterId);
  if (!currChapter) return [];

  const availChapters = currentVol.chapters.filter(
    (ch) => ch.chapter === currChapter.chapter,
  );

  const availLangs = availChapters.map((ch) => ch.translatedLanguage);

  // not everything is unique, dont show variants
  if (availLangs.length !== new Set(availLangs).size) return [];

  return availChapters;
}

export const initializeReaderManager = async (
  queryClient: () => QueryClient,
  manager: UnwrapNestedRefs<ReaderManager>,
  startingChapterId: string,
  options?: {
    quality?: ChapterImageQualities;
    pagesPerGroup?: number;
    chapterIdSequence?: string[];
    startingPage?: {
      hash: string;
      flag: boolean;
    };
  },
) => {
  let startingFlag:
    | {
        new: boolean;
        chapterId: string;
        sourceUrl: string;
        highUrl: string;
        mediumUrl: string;
        lowUrl: string;
      }
    | undefined;

  manager.setQuality(options?.quality ?? "medium");
  await manager.setPagesPerGroup(options?.pagesPerGroup ?? 1);

  manager.updateChapterIdSequence(
    options?.chapterIdSequence ?? [startingChapterId],
  );

  await manager.fetchChapterPages(queryClient, startingChapterId);

  const startingPage = options?.startingPage?.hash;
  const fetchedPages = manager.rawPageMap.get(startingChapterId)?.pages;

  if (startingPage && fetchedPages) {
    const idx =
      fetchedPages?.source.findIndex((page) => {
        const regex = /(?:high|medium|low|source)\/\d+-(?<fileName>\w+?)\.\w+/;
        const match = regex.exec(page.imageUrl);
        if (!match) return false;

        const { fileName } = match.groups!;
        return fileName.startsWith(startingPage as string);
      }) ?? -1;

    if (idx >= 0) {
      startingFlag = {
        chapterId: startingChapterId,
        new: !!options?.startingPage?.flag,
        sourceUrl: fetchedPages!.source[idx].imageUrl,
        highUrl: fetchedPages!.high[idx].imageUrl,
        mediumUrl: fetchedPages!.medium[idx].imageUrl,
        lowUrl: fetchedPages!.low[idx].imageUrl,
      };

      let url = startingFlag?.sourceUrl;
      switch (manager.quality) {
        case "high":
          url = startingFlag?.highUrl;
          break;
        case "medium":
          url = startingFlag?.mediumUrl;
          break;
        case "low":
          url = startingFlag?.lowUrl;
          break;
        default:
          url = startingFlag?.sourceUrl;
      }

      // page array should have been computed by now
      manager.currentPageIndex = Math.max(
        0,
        manager.pageArray.findIndex(
          (page) =>
            page.chapterId === startingChapterId &&
            isImagePageGroup(page) &&
            page.pages.some((p) => p.serverSrc === url),
        ),
      );
    }
  } else {
    // ensure that we start on a content page
    manager.currentPageIndex = manager.pageArray.findIndex(
      (page) =>
        page.chapterId === startingChapterId && isContentPageGroup(page),
    );
  }

  const startTime = Date.now();
  // preload starting image
  const pageOffset = manager.pageArray.findIndex(
    (page) => page.chapterId === startingChapterId && isContentPageGroup(page),
  );

  // do wait on single page
  await manager.pageMap
    .get(startingChapterId)!
    [manager.currentPageIndex - pageOffset]?.loadPages();

  // additionally queue the next page to be loaded
  manager.pageMap
    .get(startingChapterId)!
    [manager.currentPageIndex - pageOffset + 1]?.loadPages();
  console.debug(
    "%c%s",
    "color:black;background-color:#fcd4fc;padding:2px 4px;border-radius:4px;",
    "Reader Initialization",
    "Image preload done in",
    Date.now() - startTime,
    "ms",
  );

  return startingFlag;
};

// Purely manages the reading flow
export class ReaderManager {
  currentPageIndex = 0;
  chapterIdSequence: string[] = [];

  pagesPerGroup = 1;
  quality: ChapterImageQualities = "medium";

  pageArray: PageGroupTypes[] = [];

  pageCountMap: Map<string, number>;
  rawPageMap: Map<string, ChapterPageManager>;
  // map of chapters to page groups
  pageMap: Map<string, ImagePageGroup[]>;
  accessMap: Map<string, { gated: boolean; access: boolean }>;
  lockedMap: Map<string, SubscribeLockedPageGroup>;
  // format for keys is <prevId-nextId>
  interludeMap: Map<string, InterludePageGroup>;

  loadingMap: Map<string, Promise<false | undefined>>;

  constructor(
    public queryClient?: () => QueryClient,
    public auth?: AuthFunction,
  ) {
    this.pageMap = new Map();
    this.interludeMap = new Map();
    this.lockedMap = new Map();
    this.rawPageMap = new Map();
    this.pageCountMap = new Map();
    this.accessMap = new Map();
    this.loadingMap = new Map();
  }

  /**
   * Updates the image quality setting live, currently unimplemented
   * TODO: implement if necessary
   * @quality quality to use
   */
  setQuality(quality: ChapterImageQualities) {
    this.quality = quality;
  }

  /**
   * Updates the pages per group setting live
   * also manages adjusting the page index to reflect new array lengths
   * should be waited upon externally
   * @num bucket size
   */
  async setPagesPerGroup(num: number) {
    // grab information on the current page
    const currId = this.getCurrChapterId();
    const currentChapterOffset = this.pageArray.findIndex(
      (page) => page.chapterId === currId,
    );
    const currentPageOffset = this.pageArray
      .slice(currentChapterOffset, this.currentPageIndex)
      .map((page) => (isImagePageGroup(page) ? page.pages.length : 1))
      .reduce((a, c) => a + c, 0);

    // reconstruct
    this.pagesPerGroup = num;
    for (const manager of this.rawPageMap.values()) {
      this.pageMap.get(manager.chapterId)?.forEach((page) => {
        page.destroy();
      });

      this.pageMap.set(
        manager.chapterId,
        await manager.getPages(this.pagesPerGroup),
      );
    }
    this.reconstructPageArray();

    // relocate the page
    const startingIndex = this.pageArray.findIndex(
      (page) => page.chapterId === currId,
    );

    let offset = 0;
    let traverseThrough = currentPageOffset;
    while (traverseThrough > 0) {
      const page = this.pageArray[startingIndex + offset];
      if (isImagePageGroup(page)) {
        traverseThrough -= page.pages.length;
      } else {
        traverseThrough -= 1;
      }
      offset += 1;
    }

    this.currentPageIndex =
      startingIndex + offset + (traverseThrough < 0 ? -1 : 0);
  }

  updateChapterIdSequence(sequence: string[]) {
    const oldIds = new Set(this.chapterIdSequence);
    const newIds = new Set(sequence);

    // TODO: Do not do this once baseline is safari 17/iOS 17
    const toBeRemoved =
      typeof oldIds.difference === "function"
        ? oldIds.difference(newIds)
        : new Set([...oldIds].filter((id) => !newIds.has(id)));

    debug(
      "Sequence updated:",
      JSON.parse(
        JSON.stringify({
          old: this.chapterIdSequence,
          new: sequence,
        }),
      ),
    );

    for (const id of toBeRemoved.values()) {
      this.rawPageMap.get(id)?.destroy();
      this.pageMap.delete(id);
    }

    this.chapterIdSequence = sequence;
    this.reconstructPageArray();
  }

  getCurrChapterId() {
    return this.pageArray[this.currentPageIndex]?.chapterId ?? null;
  }

  getNextChapterId() {
    const currentId = this.pageArray[this.currentPageIndex]?.chapterId;
    if (!currentId) return null;
    const sequenceIndex = this.chapterIdSequence.indexOf(currentId);
    return sequenceIndex < 0
      ? null
      : (this.chapterIdSequence.at(sequenceIndex + 1) ?? null);
  }

  getPrevChapterId() {
    const currentId = this.pageArray[this.currentPageIndex]?.chapterId;
    if (!currentId) return null;
    const sequenceIndex = this.chapterIdSequence.indexOf(currentId);
    return sequenceIndex < 0
      ? null
      : (this.chapterIdSequence.at(sequenceIndex - 1) ?? null);
  }

  /**
   * Requests for all of the images in a specified chapter to be loaded
   * @queryClient query client used to make the request, handles caching
   * @chapterId id of chapter pages to fetch
   */
  async fetchChapterPages(queryClient: () => QueryClient, chapterId: string) {
    const chapterLocks = this.accessMap.get(chapterId);
    const gated = chapterLocks?.gated;
    const access = chapterLocks?.access;
    const key = `${chapterId}-${!gated || !!access}`;
    if (this.rawPageMap.has(key)) return this.rawPageMap.get(key)!;
    if (this.loadingMap.has(key)) {
      return this.loadingMap.get(key)!;
    }

    const promise = new Promise<false | undefined>(async (resolve, reject) => {
      await this._fetchChapterPages(queryClient, chapterId)
        .then(resolve)
        .catch(reject);
      this.loadingMap.delete(key);
    });

    this.loadingMap.set(key, promise);
    return promise;
  }

  async _fetchChapterPages(queryClient: () => QueryClient, chapterId: string) {
    if (!this.chapterIdSequence.includes(chapterId)) return false;
    if (this.pageMap.has(chapterId)) return;
    const chapterLocks = this.accessMap.get(chapterId);
    const gated = chapterLocks?.gated;
    const access = chapterLocks?.access;

    if (!this.rawPageMap.has(chapterId))
      this.rawPageMap.set(
        chapterId,
        new ChapterPageManager(
          queryClient,
          chapterId,
          this.quality,
          this.auth,
          gated || !access,
        ),
      );
    const startTime = Date.now();
    // debugChapterId(chapterId, "fetching pages");
    const pageManager = this.rawPageMap.get(chapterId)!;
    const pages = await pageManager.getPages(this.pagesPerGroup);

    this.pageMap.set(chapterId, pages);
    this.pageCountMap.set(chapterId, pageManager.pages!.source.length);

    if (!pages.length) {
      this.lockedMap.set(chapterId, new SubscribeLockedPageGroup(chapterId));
      if (!this.accessMap.has(chapterId))
        this.accessMap.set(chapterId, { gated: true, access: false });
    }

    debugChapterId(chapterId, "fetched pages in", Date.now() - startTime, "ms");
    this.reconstructPageArray();
  }

  /**
   * Requests for all of the images in a specified chapter to be loaded
   * @chapterId specified chapter
   * @reverse load in reverse order
   */
  async loadChapterPages(chapterId: string, reverse = false) {
    if (!this.rawPageMap.has(chapterId)) return;
    const pages = this.rawPageMap.get(chapterId)!;
    return pages.loadPages(reverse);
  }

  /**
   * Constructs the entire page array with placeholders and interludes for missing data
   * this needs to be virtualized externally for performance reasons
   */
  reconstructPageArray() {
    if (!this.chapterIdSequence.length) return;
    const startTime = Date.now();
    // debug("Reconstructing page array");
    const oldIndex = this.currentPageIndex;
    const currChapter = this.pageArray.at(oldIndex)?.chapterId ?? null;
    const offset = currChapter
      ? this.pageArray.findIndex((page) => page.chapterId === currChapter)
      : 0;

    const newArray = [];
    let currIdx = 0;

    while (currIdx < this.chapterIdSequence.length) {
      let prevId = this.chapterIdSequence.at(currIdx - 1);
      let currId = this.chapterIdSequence.at(currIdx);
      let nextId = this.chapterIdSequence.at(currIdx + 1);

      if (!currId) break;

      const currPages = this.pageMap.get(currId) ?? [];

      // use a single placeholder if we dont have pages for this chapter
      if (!this.pageMap.has(currId)) {
        if (this.queryClient) {
          const cached = getChapterFromCache(this.queryClient, currId);
          if (cached) this.pageCountMap.set(currId, cached.attributes.pages);
        }
        // newArray.push(
        //   ...Array(this.pageCountMap.get(currId) ?? 1)
        //     .fill(0)
        //     .map((_, index) => new PlaceholderPageGroup(currId)),
        // );
        newArray.push(new PlaceholderPageGroup(currId));
      } else if (!currPages.length) newArray.push(this.lockedMap.get(currId)!);
      else newArray.push(...currPages);

      if (prevId) {
        const interludeKey = `${prevId}-${currId}`;

        if (!this.interludeMap.has(interludeKey)) {
          this.interludeMap.set(
            interludeKey,
            new InterludePageGroup({
              chapterId: currId,
              prevChapterId: prevId || null,
              nextChapterId: nextId || null,
              type: nextId ? "middle" : "end",
            }),
          );
        }

        newArray.push(this.interludeMap.get(interludeKey)!);
      }

      currIdx += 1;
    }

    if (Date.now() - startTime > 50)
      debug("Reconstruction finished in", Date.now() - startTime, "ms");

    const newOffset = currChapter
      ? newArray.findIndex((page) => page.chapterId === currChapter)
      : 0;

    this.currentPageIndex += newOffset - offset;
    this.pageArray = newArray;
    return newArray;
  }

  /**
   * Destroys the reader's resources, cancels all pending requests for images
   */
  destroy() {
    this.pageMap.clear();
    for (const data of Array.from(this.rawPageMap.values())) {
      data.destroy();
    }
    this.rawPageMap.clear();
  }
}
